export interface RawSalesMetricsRow {
  store_count: number
  cases_sold: number
  volume_dollars: number
  case_cost: number
  units_sold: number | null
}

/** Normalize raw API / metrics fields for forecast transform (shared with paired forecast+actual rows). */
export function extractRawSalesMetricsForTransform(raw: any, default_case_cost: number): RawSalesMetricsRow {
  const raw_case_cost = raw.case_cost ?? raw.metrics?.case_cost ?? default_case_cost
  const numeric_case_cost =
    typeof raw_case_cost === "string" ? parseFloat(raw_case_cost) : typeof raw_case_cost === "number" ? raw_case_cost : 0
  return {
    store_count: Number(raw.store_count ?? raw.metrics?.store_count ?? 0),
    // The exact fraction when the row stored one (7 doors × 1.2 = 8.4); `cases_sold` is its rounding.
    cases_sold: Number(raw.cases_decimal ?? raw.cases_sold ?? raw.metrics?.cases_sold ?? 0),
    volume_dollars: Number(raw.volume_dollars ?? raw.metrics?.volume_dollars ?? 0),
    case_cost: numeric_case_cost,
    units_sold: raw.units_sold ?? raw.metrics?.units_sold ?? null,
  }
}

/**
 * When the API returns separate forecast and actual rows for the same calendar period,
 * attach `_paired_actual` on the forecast row so the UI can merge metrics without
 * last-write-wins on `period_key`.
 */
export function mergeForecastActualRowsForSamePeriod(rows: any[]): any[] {
  const byPeriod = new Map<string, any[]>()
  for (const r of rows) {
    const pk = String(r.period ?? r.period_key ?? "")
    if (!pk) continue
    let arr = byPeriod.get(pk)
    if (!arr) {
      arr = []
      byPeriod.set(pk, arr)
    }
    arr.push(r)
  }
  const result: any[] = []
  for (const [_pk, group] of byPeriod) {
    if (group.length === 1) {
      result.push(group[0])
      continue
    }
    const actualRow = group.find((r: any) => r.source_kind === "actual")
    // Prefer the plain forecast row for pairing; other non-actual kinds
    // (e.g. volume_based_estimate) are kept as their own rows below.
    const forecastRow =
      group.find((r: any) => r.source_kind === "forecast") ?? group.find((r: any) => r.source_kind !== "actual")
    if (forecastRow && actualRow) {
      result.push({ ...forecastRow, _paired_actual: actualRow })
      for (const r of group) {
        if (r !== forecastRow && r !== actualRow) {
          result.push(r)
        }
      }
    } else {
      result.push(...group)
    }
  }
  return result
}

/**
 * Whether the *forecast* side of a merged forecast+actual cell is the authoritative one.
 *
 * It never is: when a cell has both rows the **actual wins**. That is the rule the server
 * applies when it totals a version (`leaf_primary_totals_from_version_rows`), the rule the
 * KPI cards describe ("if a leaf month has both an actual and a forecast row, only the actual
 * counts"), and the rule `select_primary_sales_rows` defers to ("actual rows are never
 * shadowed — the actual-wins rule applies downstream").
 *
 * This used to be keyed off `period >= forecast_start_period`, which meant every actualized
 * month *inside* the plan horizon (i.e. all of them — actualization advances
 * `actuals_through_period`, never `forecast_start_period`) displayed its forecast and reported
 * $0 actualized, while the server's summary for the same version reported the actual. The
 * forecast side is still carried on the row (`variance`, compare mode), just not as the value.
 */
export function use_forecast_as_primary_for_paired_cell(): boolean {
  return false
}

/**
 * `is_locked` for a merged forecast+actual row.
 *
 * The merged row is saved under the *primary* row's `source_kind`, so it has to carry that same
 * row's lock flag: the server's upsert refuses to touch a locked cell unless the payload asserts
 * the lock, and it refuses silently. Sending the forecast row's `is_locked = false` for a row
 * saved as `actual` therefore makes every edit to an imported (locked) actual cell vanish on the
 * next load, with a 200 and a changelog entry to say it worked.
 */
export function lock_flag_for_merged_row(
  forecast_row: { is_locked?: boolean },
  paired_actual: { is_locked?: boolean } | undefined,
  use_forecast_primary: boolean
): boolean | undefined {
  if (paired_actual && !use_forecast_primary) {
    return paired_actual.is_locked
  }
  return forecast_row.is_locked
}

/**
 * `data_source` / `manual_note` for a merged forecast+actual row.
 *
 * Same rule as `lock_flag_for_merged_row`: the merged row is saved under the *primary* row's
 * `source_kind`, so its provenance fields must travel with that same row. Carrying the forecast
 * row's `data_source` onto a row saved as `actual` overwrote an imported actual's provenance
 * (e.g. `quickbooks`) with the forecast side's on every hierarchy save.
 */
export function provenance_field_for_merged_row<T>(
  forecast_value: T,
  actual_value: T,
  paired_actual: object | undefined,
  use_forecast_primary: boolean
): T {
  if (paired_actual && !use_forecast_primary) {
    return actual_value
  }
  return forecast_value
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  describe("provenance_field_for_merged_row", () => {
    it("takes the actual row's provenance when the actual is primary", () => {
      const paired = { data_source: "quickbooks" }
      expect(provenance_field_for_merged_row("promomash_forecast", paired.data_source, paired, false)).toBe(
        "quickbooks"
      )
    })

    it("takes the forecast row's provenance when there is no paired actual", () => {
      expect(provenance_field_for_merged_row("promomash_forecast", undefined, undefined, false)).toBe(
        "promomash_forecast"
      )
    })

    it("takes the forecast row's provenance when the forecast is primary", () => {
      expect(provenance_field_for_merged_row("promomash_forecast", "quickbooks", { }, true)).toBe(
        "promomash_forecast"
      )
    })
  })

  describe("use_forecast_as_primary_for_paired_cell", () => {
    it("never prefers the forecast row — a paired actual is authoritative", () => {
      // Regression: the old rule (period >= forecast_start_period) made the grid show the
      // forecast for actualized months and report $0 actualized, disagreeing with the server.
      expect(use_forecast_as_primary_for_paired_cell()).toBe(false)
    })

    it("keeps the merged row's lock flag on the actual side, matching the actual-wins rule", () => {
      const primary_is_forecast = use_forecast_as_primary_for_paired_cell()
      expect(lock_flag_for_merged_row({ is_locked: false }, { is_locked: true }, primary_is_forecast)).toBe(true)
    })
  })

  describe("lock_flag_for_merged_row", () => {
    it("takes the paired actual's lock when the actual row is primary", () => {
      // Promomash imports lock every non-forecast row, so this is the historical-cell case.
      expect(lock_flag_for_merged_row({ is_locked: false }, { is_locked: true }, false)).toBe(true)
    })

    it("takes the forecast row's lock when the forecast row is primary", () => {
      expect(lock_flag_for_merged_row({ is_locked: false }, { is_locked: true }, true)).toBe(false)
    })

    it("takes the forecast row's lock when there is no paired actual", () => {
      expect(lock_flag_for_merged_row({ is_locked: true }, undefined, false)).toBe(true)
    })
  })

  describe("mergeForecastActualRowsForSamePeriod", () => {
    it("pairs forecast + actual for the same period into one row", () => {
      const merged = mergeForecastActualRowsForSamePeriod([
        { period: "2026-03", source_kind: "forecast", volume_dollars: 100 },
        { period: "2026-03", source_kind: "actual", volume_dollars: 80 },
      ])
      expect(merged).toHaveLength(1)
      expect(merged[0].volume_dollars).toBe(100)
      expect(merged[0]._paired_actual.volume_dollars).toBe(80)
    })

    it("leaves a single row unchanged", () => {
      const merged = mergeForecastActualRowsForSamePeriod([
        { period: "2026-03", source_kind: "forecast", volume_dollars: 50 },
      ])
      expect(merged).toHaveLength(1)
      expect(merged[0]._paired_actual).toBeUndefined()
    })

    it("keeps other non-actual rows instead of dropping them", () => {
      const merged = mergeForecastActualRowsForSamePeriod([
        { period: "2026-03", source_kind: "volume_based_estimate", volume_dollars: 70 },
        { period: "2026-03", source_kind: "forecast", volume_dollars: 100 },
        { period: "2026-03", source_kind: "actual", volume_dollars: 80 },
      ])
      expect(merged).toHaveLength(2)
      const paired = merged.find((r: any) => r._paired_actual)
      expect(paired.source_kind).toBe("forecast")
      expect(paired._paired_actual.volume_dollars).toBe(80)
      const estimate = merged.find((r: any) => r.source_kind === "volume_based_estimate")
      expect(estimate.volume_dollars).toBe(70)
    })
  })
}
